import React, { FC, useState, ChangeEvent } from "react";
import { Form, Dropdown, Icon } from "semantic-ui-react";
import type { SemanticICONS } from "semantic-ui-react";
import { BasicDialog } from "@src/views/components/modals/BasicDialog";
import { BasicDialogContainer } from "@src/views/components/modals/BasicDialogContainer";
import { Align, SemanticUIColor, Size } from "@src/types";
import { useCentralCalls } from "@src/rest/CentralCalls";
import { Grid, Cell } from "styled-css-grid";
import "./DashDialogs.scss";

interface Props {
    onPageCreated?: () => void;
    onClose?: () => void;
}

// Common dashboard icons
const DashboardIcons: Array<{ value: SemanticICONS; display: string }> = [
    { display: 'Add', value: 'add' },
    { display: 'Alarm', value: 'alarm' },
    { display: 'Anchor', value: 'anchor' },
    { display: 'Archive', value: 'archive' },
    { display: 'Area Chart', value: 'area chart' },
    { display: 'Arrow Circle Down', value: 'arrow circle down' },
    { display: 'Arrow Circle Left', value: 'arrow circle left' },
    { display: 'Arrow Circle Right', value: 'arrow circle right' },
    { display: 'Arrow Circle Up', value: 'arrow circle up' },
    { display: 'Assistive Listening Systems', value: 'assistive listening systems' },
    { display: 'Asterisk', value: 'asterisk' },
    { display: 'At', value: 'at' },
    { display: 'Balance Scale', value: 'balance scale' },
    { display: 'Ban', value: 'ban' },
    { display: 'Barcode', value: 'barcode' },
    { display: 'Battery Full', value: 'battery full' },
    { display: 'Beer', value: 'beer' },
    { display: 'Bell', value: 'bell' },
    { display: 'Bicycle', value: 'bicycle' },
    { display: 'Binoculars', value: 'binoculars' },
    { display: 'Birthday Cake', value: 'birthday cake' },
    { display: 'Bomb', value: 'bomb' },
    { display: 'Book', value: 'book' },
    { display: 'Bookmark', value: 'bookmark' },
    { display: 'Briefcase', value: 'briefcase' },
    { display: 'Bug', value: 'bug' },
    { display: 'Bullhorn', value: 'bullhorn' },
    { display: 'Calculator', value: 'calculator' },
    { display: 'Calendar', value: 'calendar' },
    { display: 'Camera', value: 'camera' },
    { display: 'Car', value: 'car' },
    { display: 'Chart Bar', value: 'chart bar' },
    { display: 'Chart Line', value: 'chart line' },
    { display: 'Chart Pie', value: 'chart pie' },
    { display: 'Check Circle', value: 'check circle' },
    { display: 'Cloud', value: 'cloud' },
    { display: 'Coffee', value: 'coffee' },
    { display: 'Cogs', value: 'cogs' },
    { display: 'Compass', value: 'compass' },
    { display: 'Database', value: 'database' },
    { display: 'Download', value: 'download' },
    { display: 'Envelope', value: 'envelope' },
    { display: 'Eye', value: 'eye' },
    { display: 'Film', value: 'film' },
    { display: 'Flag', value: 'flag' },
    { display: 'Folder', value: 'folder' },
    { display: 'Gift', value: 'gift' },
    { display: 'Globe', value: 'globe' },
    { display: 'Heart', value: 'heart' },
    { display: 'Home', value: 'home' },
    { display: 'Hourglass', value: 'hourglass' },
    { display: 'Id Badge', value: 'id badge' },
    { display: 'Id Card', value: 'id card' },
    { display: 'Image', value: 'image' },
    { display: 'Inbox', value: 'inbox' },
    { display: 'Industry', value: 'industry' },
    { display: 'Info Circle', value: 'info circle' },
    { display: 'Key', value: 'key' },
    { display: 'Keyboard', value: 'keyboard' },
    { display: 'Laptop', value: 'laptop' },
    { display: 'Leaf', value: 'leaf' },
    { display: 'Lemon', value: 'lemon' },
    { display: 'Life Ring', value: 'life ring' },
    { display: 'Lightbulb', value: 'lightbulb' },
    { display: 'Lock', value: 'lock' },
    { display: 'Magic', value: 'magic' },
    { display: 'Magnet', value: 'magnet' },
    { display: 'Map', value: 'map' },
    { display: 'Map Marker', value: 'map marker' },
    { display: 'Medal', value: 'trophy' },
    { display: 'Microphone', value: 'microphone' },
    { display: 'Mobile', value: 'mobile' },
    { display: 'Money', value: 'money' },
    { display: 'Moon', value: 'moon' },
    { display: 'Motorcycle', value: 'motorcycle' },
    { display: 'Newspaper', value: 'newspaper' },
    { display: 'Paint Brush', value: 'paint brush' },
    { display: 'Paper Plane', value: 'paper plane' },
    { display: 'Paw', value: 'paw' },
    { display: 'Pen', value: 'pencil' },
    { display: 'Pencil', value: 'pencil' },
    { display: 'Phone', value: 'phone' },
    { display: 'Plane', value: 'plane' },
    { display: 'Plug', value: 'plug' },
    { display: 'Print', value: 'print' },
    { display: 'Puzzle Piece', value: 'puzzle piece' },
    { display: 'Rocket', value: 'rocket' },
    { display: 'Save', value: 'save' },
    { display: 'School', value: 'university' },
    { display: 'Search', value: 'search' },
    { display: 'Shield', value: 'shield' },
    { display: 'Shopping Bag', value: 'shopping bag' },
    { display: 'Shopping Cart', value: 'shopping cart' },
    { display: 'Sign-in', value: 'sign-in' },
    { display: 'Sign-out', value: 'sign-out' },
    { display: 'Smile', value: 'smile' },
    { display: 'Snowflake', value: 'snowflake' },
    { display: 'Star', value: 'star' },
    { display: 'Sun', value: 'sun' },
    { display: 'Sync', value: 'sync' },
    { display: 'Tablet', value: 'tablet' },
    { display: 'Tag', value: 'tag' },
    { display: 'Taxi', value: 'taxi' },
    { display: 'Terminal', value: 'terminal' },
    { display: 'Thermometer', value: 'thermometer' },
    { display: 'Thumbs Up', value: 'thumbs up' },
    { display: 'Thumbs Down', value: 'thumbs down' },
    { display: 'Ticket', value: 'ticket' },
    { display: 'Times Circle', value: 'times circle' },
    { display: 'Trash', value: 'trash' },
    { display: 'Tree', value: 'tree' },
    { display: 'Trophy', value: 'trophy' },
    { display: 'Truck', value: 'truck' },
    { display: 'Tv', value: 'tv' },
    { display: 'Umbrella', value: 'umbrella' },
    { display: 'University', value: 'university' },
    { display: 'Unlock', value: 'unlock' },
    { display: 'User', value: 'user' },
    { display: 'Users', value: 'users' },
    { display: 'Video', value: 'video' },
    { display: 'Volume Up', value: 'volume up' },
    { display: 'Volume Down', value: 'volume down' },
    { display: 'Wifi', value: 'wifi' },
    { display: 'Wrench', value: 'wrench' },
    { display: 'Balance', value: 'balance' },
    { display: 'View', value: 'eye' },
    { display: 'Help', value: 'help' },
    { display: 'Bug', value: 'bug' }
] as const;

const DropdownLabel: FC<{ value: SemanticICONS; text: string }> = ({ value, text }) => (
    <div className="dash-dialog__selected-icon">
        <Icon name={value} color="teal" />
        {text}
    </div>
);

const CreateDashPageDialog: FC<Props> = ({ onPageCreated, onClose }) => {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [icon, setIcon] = useState<SemanticICONS>("file text");
    const { RunInsert } = useCentralCalls();

    const handleSubmit = async () => {
        if (!title.trim()) {
            return;
        }

        try {
            await RunInsert("DashPage", {
                Name: title,
                Description: description,
                PageConfig: "[]",
                Icon: icon,
            });

            if (onPageCreated) {
                onPageCreated();
            }
            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error("Error creating dash page:", error);
        }
    };

    return (
        <BasicDialog
            showCancelButton={true}
            showSubmitButton={true}
            submitLabel="Create Page"
            submitColor={SemanticUIColor.PURPLE}
            onCancel={onClose}
            onSubmit={handleSubmit}
            controlAlign={Align.CENTER}
            className="no-padding dash-dialog"
        >
            <BasicDialogContainer
                title="Create New Dashboard Page"
                titleColor={SemanticUIColor.PURPLE}
                contentPadding={[Size.MD, Size.MD, Size.LG, Size.MD]}
                onClose={onClose}
            >
                <Form>
                    <Grid>
                        <Cell width={16}>
                            <Form.Field className="dash-dialog__form-field">
                                <label>Page Name</label>
                                <Form.Input
                                    placeholder="Enter page name"
                                    value={title}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                                />
                            </Form.Field>
                        </Cell>

                        <Cell width={16}>
                            <Form.Field className="dash-dialog__form-field">
                                <label>Description</label>
                                <Form.TextArea
                                    placeholder="Enter page description"
                                    value={description}
                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                                    className="dash-dialog__description"
                                />
                            </Form.Field>
                        </Cell>

                        <Cell width={16}>
                            <Form.Field className="dash-dialog__form-field">
                                <label>Icon</label>
                                <Dropdown
                                    placeholder="Select an Icon"
                                    fluid
                                    selection
                                    className="dash-dialog__icon-dropdown"
                                    options={DashboardIcons.map(({ display, value }) => ({
                                        key: value,
                                        value,
                                        text: display,
                                        content: (
                                            <div>
                                                <Icon name={value} color="teal" />
                                                {display}
                                            </div>
                                        ),
                                    }))}
                                    value={icon}
                                    onChange={(_, { value }) => setIcon(value as SemanticICONS)}
                                    renderLabel={(item: any) => ({
                                        content: <DropdownLabel value={item.value} text={item.text} />,
                                        as: 'a'
                                    })}
                                />
                            </Form.Field>
                        </Cell>
                    </Grid>
                </Form>
            </BasicDialogContainer>
        </BasicDialog>
    );
};

export { CreateDashPageDialog };
